import React, { useState, useEffect } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { DatePicker, Button, Space } from 'antd';
import moment from 'moment';

import { useClients } from '../../../models/useClients';
import { AdminClientsList, ClientsSearch } from '../../../components/admin/AdminClientsComponents';
import CenteredSpinner from '../../../components/CenteredSpinner';

const { RangePicker } = DatePicker;

function escapeCsvValue(value) {
    const text = value === null || value === undefined ? '' : `${value}`;
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

function toIsoString(ts) {
    if (!ts) return '';
    if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
    if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toISOString();
    if (ts instanceof Date) return ts.toISOString();
    return '';
}

export default function AdminStatusClientsBody({ status, locStrings, isAdmin }) {
    const [loading, clients] = useClients({ status: status });
    const [searchText, setSearchText] = useState('');
    const [searching, setSearching] = useState(false);
    const [isSearchDisabled, setIsSearchDisabled] = useState(false);
    const [filteredClients, setFilteredClients] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    useEffect(() => {
        executeSearch(searchText);
    }, [searchText, clients, dateRange]);

    const executeSearch = (value) => {
        const words = (value || '')
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        const start = dateRange && dateRange[0] ? dateRange[0].startOf('day').valueOf() : null;
        const end = dateRange && dateRange[1] ? dateRange[1].endOf('day').valueOf() : null;

        const filtered = (clients || []).filter((client) => {
            const createdAtMillis = client.createdAt && client.createdAt.toMillis ? client.createdAt.toMillis() : null;
            const inDateRange = (!start || (createdAtMillis && createdAtMillis >= start))
                && (!end || (createdAtMillis && createdAtMillis <= end));
            if (!inDateRange) return false;
            if (words.length === 0) return true;

            const haystack = [
                client.email,
                client.displayName,
                client.phoneNumber,
                client.employerName,
                client.profileBio,
                client.country,
                client.categoryCode,
                client.categoryName,
                client.type,
                client.authProvider,
                Array.isArray(client.authProviders) ? client.authProviders.join(' ') : null,
                client.idLast4,
                client.originReason,
                client.originPath,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return words.every((w) => haystack.includes(w));
        });

        setFilteredClients((words.length > 0 || start || end) ? filtered : null);
        setSearching(words.length > 0);
        setIsSearchDisabled(false);
        return filtered;
    };

    const onSearch = (value) => {
        setSearchText('');
        setSearchText(value);
    };

    const exportClientsCsv = () => {
        const exportList = filteredClients || clients || [];
        const rows = [
            [
                'clientId',
                'displayName',
                'email',
                'status',
                'type',
                'categoryCode',
                'categoryName',
                'marketingOptIn',
                'createdAt',
                'updatedAt',
                'authProvider',
                'platform',
                'originReason',
                'originPath',
                // Why is this client paused (last pause only; empty = no claim).
                // 'auth_user_deleted' = the onDeleteAuthUser sweep; 'manual' = admin.
                'pausedReason',
                'pausedAt',
                // Server-observed terms-acceptance IP (write-once; empty = never recorded).
                'agreedTermsIP',
            ],
            ...exportList.map((client) => [
                client.id || '',
                client.displayName || '',
                client.email || '',
                client.status || '',
                client.type || '',
                client.categoryCode || '',
                client.categoryName || '',
                client.marketingOptIn === true ? 'true' : client.marketingOptIn === false ? 'false' : '',
                toIsoString(client.createdAt),
                toIsoString(client.updatedAt),
                client.authProvider || '',
                client.platform || '',
                client.originReason || '',
                client.originPath || '',
                `${client.pausedReason || ''}`.trim(),
                toIsoString(client.pausedAt),
                `${client.agreedTermsIP || ''}`.trim(),
            ]),
        ];
        const csv = rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `admin-clients-${status || 'all'}-${moment().format('YYYYMMDD-HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (!clients) {
        return <CenteredSpinner loading={true} />;
    }

    if (!isAdmin) {
        return <div>You are not authorized to view this page.</div>;
    }

    return (
        <div>
            <ClientsSearch initialValue={searchText} callbackOnSearch={onSearch} loading={searching} disabled={isSearchDisabled} />
            <Space style={{ marginTop: 8, marginBottom: 8 }}>
                <RangePicker value={dateRange} onChange={(range) => setDateRange(range || [null, null])} allowClear />
                <Button onClick={exportClientsCsv} disabled={!((filteredClients || clients || []).length)}>
                    Export CSV
                </Button>
            </Space>
            <AdminClientsList condition={() => { return !loading; }} clients={filteredClients || clients} />
        </div>
    );
}
